import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Eye, FileText, Trash2, Edit, Printer, Search, X } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const Invoices = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [proformas, setProformas] = useState([]);
  const [clients, setClients] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [portCalls, setPortCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [creationType, setCreationType] = useState('proforma'); // 'proforma', 'combine' or 'standalone'
  const [selectedProformaIds, setSelectedProformaIds] = useState([]); // Birleştirilecek proformalar
  const [formData, setFormData] = useState({
    proforma_id: '',
    client_id: '',
    vessel_name: '',
    port: '',
    currency: 'USD',
    due_days: 5,
    notes: '',
    lines: [{ description: '', quantity: 1, unit: 'ADET', unit_price: 0, has_vat: false, vat_rate: 20, total: 0 }]
  });
  const [editFormData, setEditFormData] = useState({
    vessel_name: '',
    port: '',
    currency: 'USD',
    due_days: 5,
    notes: '',
    lines: []
  });

  useEffect(() => {
    loadData();

    // Otomatik yenileme: Her 30 saniyede bir
    const intervalId = setInterval(() => {
      loadData();
    }, 30000);

    // Sayfa odaklandığında yenile
    const handleFocus = () => {
      loadData();
    };
    window.addEventListener('focus', handleFocus);

    // Cleanup
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const loadData = async () => {
    try {
      const [invoicesRes, proformasRes, clientsRes, vesselsRes, portCallsRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/proformas?status=approved'),
        api.get('/clients'),
        api.get('/vessels'),
        api.get('/port-calls')
      ]);
      setInvoices(invoicesRes.data);
      setProformas(proformasRes.data);
      setClients(clientsRes.data);
      setVessels(vesselsRes.data);
      setPortCalls(portCallsRes.data);
    } catch (error) {
      // İlk yüklemede hata göster, otomatik yenilemelerde sessiz başarısız ol
      if (loading) {
        toast.error('Veriler yüklenemedi');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    if (field === 'quantity' || field === 'unit_price' || field === 'has_vat' || field === 'vat_rate') {
      const baseTotal = (newLines[index].quantity || 0) * (newLines[index].unit_price || 0);
      const vatAmount = newLines[index].has_vat ? baseTotal * (newLines[index].vat_rate || 20) / 100 : 0;
      newLines[index].total = baseTotal + vatAmount;
    }
    setFormData({ ...formData, lines: newLines });
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { description: '', quantity: 1, unit: 'ADET', unit_price: 0, has_vat: false, vat_rate: 20, total: 0 }]
    });
  };

  const removeLine = (index) => {
    if (formData.lines.length > 1) {
      const newLines = formData.lines.filter((_, i) => i !== index);
      setFormData({ ...formData, lines: newLines });
    }
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalVat = 0;
    
    formData.lines.forEach(line => {
      const baseTotal = (line.quantity || 0) * (line.unit_price || 0);
      const vatAmount = line.has_vat ? baseTotal * (line.vat_rate || 20) / 100 : 0;
      subtotal += baseTotal;
      totalVat += vatAmount;
    });
    
    const total = subtotal + totalVat;
    return { subtotal, totalVat, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let submitData;
      
      if (creationType === 'proforma') {
        submitData = { proforma_id: formData.proforma_id, due_days: formData.due_days, notes: formData.notes };
      } else if (creationType === 'combine') {
        if (selectedProformaIds.length < 2) {
          toast.error('En az 2 proforma seçmelisiniz');
          return;
        }
        submitData = { proforma_ids: selectedProformaIds, due_days: formData.due_days, notes: formData.notes };
      } else {
        submitData = formData;
      }
      
      await api.post('/invoices', submitData);
      toast.success('Fatura başarıyla oluşturuldu');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fatura oluşturulamadı');
    }
  };

  const resetForm = () => {
    setFormData({
      proforma_id: '',
      client_id: '',
      vessel_name: '',
      port: '',
      currency: 'USD',
      due_days: 5,
      notes: '',
      lines: [{ description: '', quantity: 1, unit: 'ADET', unit_price: 0, has_vat: false, vat_rate: 20, total: 0 }]
    });
    setCreationType('proforma');
    setSelectedProformaIds([]);
  };

  // Proforma seçim toggle fonksiyonu
  const toggleProformaSelection = (proformaId) => {
    setSelectedProformaIds(prev => {
      if (prev.includes(proformaId)) {
        return prev.filter(id => id !== proformaId);
      } else {
        return [...prev, proformaId];
      }
    });
  };

  // Seçili proformaların toplam tutarını hesapla
  const getSelectedProformasTotal = () => {
    return proformas
      .filter(p => selectedProformaIds.includes(p.id))
      .reduce((sum, p) => sum + (p.total || 0), 0);
  };

  // Seçili proformaların müşterisini kontrol et (hepsi aynı müşteriye mi ait)
  const getSelectedProformasClient = () => {
    const selected = proformas.filter(p => selectedProformaIds.includes(p.id));
    if (selected.length === 0) return null;
    const clientIds = [...new Set(selected.map(p => p.client_id))];
    if (clientIds.length > 1) return 'mixed';
    return selected[0].client_name;
  };

  const handleViewDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetail(true);
  };

  const handleDelete = async (invoiceId, invoiceNumber) => {
    if (!window.confirm(`"${invoiceNumber}" faturasını silmek istediğinizden emin misiniz?`)) return;
    
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success('Fatura başarıyla silindi');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fatura silinemedi');
    }
  };

  // Edit Functions
  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setEditFormData({
      vessel_name: invoice.vessel_name || '',
      port: invoice.port || '',
      currency: invoice.currency || 'USD',
      due_days: invoice.due_days || 5,
      notes: invoice.notes || '',
      lines: invoice.lines?.map(line => ({
        description: line.description || '',
        quantity: line.quantity || 1,
        unit: line.unit || 'ADET',
        unit_price: line.unit_price || 0,
        has_vat: line.has_vat || false,
        vat_rate: line.vat_rate || 20,
        total: line.total || 0
      })) || [{ description: '', quantity: 1, unit: 'ADET', unit_price: 0, has_vat: false, vat_rate: 20, total: 0 }]
    });
    setShowEditModal(true);
  };

  const handleEditLineChange = (index, field, value) => {
    const newLines = [...editFormData.lines];
    newLines[index][field] = value;
    if (field === 'quantity' || field === 'unit_price' || field === 'has_vat' || field === 'vat_rate') {
      const baseTotal = (newLines[index].quantity || 0) * (newLines[index].unit_price || 0);
      const vatAmount = newLines[index].has_vat ? baseTotal * (newLines[index].vat_rate || 20) / 100 : 0;
      newLines[index].total = baseTotal + vatAmount;
    }
    setEditFormData({ ...editFormData, lines: newLines });
  };

  const addEditLine = () => {
    setEditFormData({
      ...editFormData,
      lines: [...editFormData.lines, { description: '', quantity: 1, unit: 'ADET', unit_price: 0, has_vat: false, vat_rate: 20, total: 0 }]
    });
  };

  const removeEditLine = (index) => {
    if (editFormData.lines.length > 1) {
      const newLines = editFormData.lines.filter((_, i) => i !== index);
      setEditFormData({ ...editFormData, lines: newLines });
    }
  };

  const calculateEditTotals = () => {
    let subtotal = 0;
    let totalVat = 0;
    
    editFormData.lines.forEach(line => {
      const baseTotal = (line.quantity || 0) * (line.unit_price || 0);
      const vatAmount = line.has_vat ? baseTotal * (line.vat_rate || 20) / 100 : 0;
      subtotal += baseTotal;
      totalVat += vatAmount;
    });
    
    const total = subtotal + totalVat;
    return { editSubtotal: subtotal, editTotalVat: totalVat, editTotal: total };
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/invoices/${editingInvoice.id}`, editFormData);
      toast.success('Fatura başarıyla güncellendi');
      setShowEditModal(false);
      setEditingInvoice(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fatura güncellenemedi');
    }
  };

  const { editSubtotal, editTotalVat, editTotal } = calculateEditTotals();

  // Fatura Yazdırma Fonksiyonu
  const printInvoice = (invoice) => {
    // Gemi bilgilerini bul
    const selectedVessel = vessels.find(v => v.name === invoice.vessel_name);
    const vesselImo = selectedVessel?.imo || '-';
    const vesselGrt = selectedVessel?.gross_tonnage || '-';
    const vesselNrt = selectedVessel?.net_tonnage || '-';
    
    // Port call bilgilerini bul (ETA/ETD için) - proforma üzerinden
    const relatedProforma = proformas.find(p => p.id === invoice.proforma_id);
    const relatedPortCall = relatedProforma ? portCalls.find(pc => pc.id === relatedProforma.port_call_id) : null;
    const etaFormatted = relatedPortCall?.eta ? new Date(relatedPortCall.eta).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
    const etdFormatted = relatedPortCall?.etd ? new Date(relatedPortCall.etd).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          @page { size: A4; margin: 15mm; }
          * { box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 25px; font-size: 12px; line-height: 1.5; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 25px; padding-bottom: 15px; border-bottom: 3px solid #1e40af; }
          .company-name { font-size: 22px; font-weight: bold; color: #1e40af; }
          .company-subtitle { font-size: 10px; color: #64748b; }
          .invoice-info { text-align: right; }
          .invoice-number { font-size: 16px; font-weight: bold; color: #1e40af; }
          .invoice-date { font-size: 11px; color: #64748b; }
          .title { font-size: 18px; text-align: center; background: linear-gradient(135deg, #1e40af, #3b82f6); color: white; padding: 10px; border-radius: 8px; margin: 20px 0; text-transform: uppercase; letter-spacing: 2px; }
          .info-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }
          .info-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 10px; }
          .info-label { font-size: 8px; text-transform: uppercase; color: #64748b; font-weight: 600; margin-bottom: 2px; }
          .info-value { font-size: 11px; font-weight: 600; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th { background: #1e40af; color: white; padding: 8px 10px; text-align: left; font-size: 10px; text-transform: uppercase; }
          td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; }
          tr:nth-child(even) { background: #f8fafc; }
          .totals { background: #f8fafc; border-radius: 8px; padding: 15px; margin-top: 20px; }
          .total-row { display: flex; justify-content: space-between; padding: 5px 0; }
          .grand-total { font-size: 18px; font-weight: bold; color: #1e40af; border-top: 2px solid #1e40af; padding-top: 10px; margin-top: 10px; }
          .footer { margin-top: 40px; text-align: center; font-size: 9px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 10px; }
          .status-badge { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 10px; font-weight: 600; }
          .status-pending { background: #fef3c7; color: #92400e; }
          .status-paid { background: #d1fae5; color: #065f46; }
          .status-overdue { background: #fee2e2; color: #991b1b; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="company-name">ROTA MARITIME AGENCY</div>
            <div class="company-subtitle">Ship Agency Services • Sinop, Turkey</div>
          </div>
          <div class="invoice-info">
            <div class="invoice-number">${invoice.number}</div>
            <div class="invoice-date">${new Date(invoice.issue_date).toLocaleDateString('tr-TR')}</div>
          </div>
        </div>
        
        <div class="title">📄 FATURA / INVOICE</div>
        
        <div class="info-grid">
          <div class="info-box">
            <div class="info-label">Müşteri / Client</div>
            <div class="info-value">${invoice.client_name}</div>
          </div>
          <div class="info-box">
            <div class="info-label">Gemi / Vessel</div>
            <div class="info-value">${invoice.vessel_name || '-'}</div>
          </div>
          <div class="info-box">
            <div class="info-label">IMO</div>
            <div class="info-value">${vesselImo}</div>
          </div>
          <div class="info-box">
            <div class="info-label">GRT / NRT</div>
            <div class="info-value">${vesselGrt} / ${vesselNrt}</div>
          </div>
          <div class="info-box">
            <div class="info-label">Liman / Port</div>
            <div class="info-value">${invoice.port || '-'}</div>
          </div>
          <div class="info-box">
            <div class="info-label">Vade Tarihi / Due Date</div>
            <div class="info-value">${new Date(invoice.due_date).toLocaleDateString('tr-TR')}</div>
          </div>
          <div class="info-box">
            <div class="info-label">Geliş / ETA</div>
            <div class="info-value">${etaFormatted}</div>
          </div>
          <div class="info-box">
            <div class="info-label">Gidiş / ETD</div>
            <div class="info-value">${etdFormatted}</div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th style="width:40%">Açıklama / Description</th>
              <th>Miktar / Qty</th>
              <th>Birim / Unit</th>
              <th>B.Fiyat / Price</th>
              <th>KDV / VAT</th>
              <th style="text-align:right">Toplam / Total</th>
            </tr>
          </thead>
          <tbody>
            ${(invoice.lines || []).map(line => {
              // Birim fiyatı 3 ondalık, toplam 2 ondalık göster
              const unitPriceFormatted = (line.unit_price || 0).toFixed(3).replace(/\.?0+$/, '') || '0';
              return `
              <tr>
                <td>${line.description}</td>
                <td>${line.quantity}</td>
                <td>${line.unit || 'ADET'}</td>
                <td>${invoice.currency} ${unitPriceFormatted}</td>
                <td>${line.has_vat ? `%${line.vat_rate || 20}` : '-'}</td>
                <td style="text-align:right"><strong>${invoice.currency} ${(line.total || 0).toFixed(2)}</strong></td>
              </tr>
            `}).join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <div class="total-row">
            <span>Ara Toplam / Subtotal:</span>
            <span>${invoice.currency} ${(invoice.subtotal || 0).toFixed(2)}</span>
          </div>
          ${(invoice.total_vat || 0) > 0 ? `
          <div class="total-row" style="color:#059669">
            <span>KDV / VAT:</span>
            <span>${invoice.currency} ${(invoice.total_vat || 0).toFixed(2)}</span>
          </div>
          ` : ''}
          <div class="total-row grand-total">
            <span>GENEL TOPLAM / GRAND TOTAL:</span>
            <span>${invoice.currency} ${(invoice.total || 0).toFixed(2)}</span>
          </div>
          <div class="total-row" style="margin-top:10px">
            <span>Ödenen / Paid:</span>
            <span style="color:#059669">${invoice.currency} ${(invoice.paid_amount || 0).toFixed(2)}</span>
          </div>
          <div class="total-row">
            <span>Kalan Bakiye / Balance:</span>
            <span style="color:${(invoice.balance || 0) > 0 ? '#dc2626' : '#059669'}">${invoice.currency} ${(invoice.balance || 0).toFixed(2)}</span>
          </div>
        </div>
        
        ${invoice.notes ? `
        <div style="margin-top:20px; padding:10px; background:#f0f9ff; border-radius:6px">
          <strong>Notlar / Notes:</strong><br/>
          ${invoice.notes}
        </div>
        ` : ''}
        
        <div class="footer">
          Rota Maritime Agency • Sinop, Turkey • ${new Date().toLocaleString('tr-TR')}
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
    toast.success('Fatura yazdırma penceresi açıldı');
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: 'badge-pending',
      partial: 'badge-partial',
      paid: 'badge-paid',
      overdue: 'badge-overdue',
      cancelled: 'badge-cancelled'
    };
    return classes[status] || 'badge-pending';
  };

  const isOverdue = (invoice) => {
    return invoice.status !== 'paid' && new Date(invoice.due_date) < new Date();
  };

  const { subtotal, totalVat, total } = calculateTotals();

  // Fatura tablosu render fonksiyonu
  const renderInvoiceTable = (invoiceList, title, bgColor) => {
    if (invoiceList.length === 0) return null;
    
    return (
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '0.75rem 1rem', 
          backgroundColor: bgColor, 
          color: 'white',
          borderRadius: '8px 8px 0 0',
          fontWeight: '600'
        }}>
          <span>{title}</span>
          <span style={{ 
            backgroundColor: 'rgba(255,255,255,0.2)', 
            padding: '0.25rem 0.75rem', 
            borderRadius: '12px',
            fontSize: '0.875rem'
          }}>
            {invoiceList.length}
          </span>
        </div>
        <div className="table-container" style={{ borderRadius: '0 0 8px 8px', border: `1px solid ${bgColor}`, borderTop: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Numara</th>
                <th>Müşteri</th>
                <th>Gemi</th>
                <th>Oluşturma</th>
                <th>Toplam</th>
                <th>Ödenen</th>
                <th>Kalan</th>
                <th>Vade</th>
                <th>Bekleyen</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {invoiceList.map((invoice) => {
                const createdDate = new Date(invoice.created_at || invoice.issue_date);
                const today = new Date();
                const daysSinceCreation = Math.floor((today - createdDate) / (1000 * 60 * 60 * 24));
                const isPaid = invoice.status === 'paid';
                
                return (
                <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                  <td><strong>{invoice.number}</strong></td>
                  <td>{invoice.client_name}</td>
                  <td>{invoice.vessel_name || '-'}</td>
                  <td style={{ fontSize: '0.85rem', color: '#64748b' }}>{createdDate.toLocaleDateString('tr-TR')}</td>
                  <td><strong>{invoice.currency} {(invoice.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></td>
                  <td style={{ color: '#059669' }}>{invoice.currency} {(invoice.paid_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td style={{ color: '#dc2626' }}>{invoice.currency} {Math.max(0, invoice.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td>{new Date(invoice.due_date).toLocaleDateString('tr-TR')}</td>
                  <td>
                    {isPaid ? (
                      <span style={{ color: '#059669', fontWeight: 600 }}>Ödendi</span>
                    ) : (
                      <span style={{ 
                        color: daysSinceCreation > 30 ? '#dc2626' : daysSinceCreation > 14 ? '#f59e0b' : '#64748b',
                        fontWeight: daysSinceCreation > 14 ? 600 : 400
                      }}>
                        {daysSinceCreation} gün
                      </span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="action-btn" onClick={() => handleViewDetail(invoice)} title="Detay"><Eye size={18} /></button>
                      <button className="action-btn" onClick={() => handleEdit(invoice)} title="Düzenle" style={{ backgroundColor: '#f59e0b', color: 'white' }}><Edit size={18} /></button>
                      <button className="action-btn" onClick={() => printInvoice(invoice)} title="Yazdır" style={{ backgroundColor: '#3b82f6', color: 'white' }}><Printer size={18} /></button>
                      <button className="action-btn action-btn-danger" onClick={() => handleDelete(invoice.id, invoice.number)} title="Sil"><Trash2 size={18} /></button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="content">
      <div className="topbar">
        <h1 className="topbar-title" data-testid="page-title">{t('invoices.title')}</h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {/* Arama Kutusu */}
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              placeholder={t('common.search') + '...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '0.5rem 2.5rem 0.5rem 2.5rem',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                width: '300px',
                fontSize: '0.875rem'
              }}
              data-testid="invoice-search"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
              >
                <X size={16} />
              </button>
            )}
          </div>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowModal(true)}
            data-testid="create-invoice-btn"
          >
            <Plus size={18} />
            Fatura Oluştur
          </button>
        </div>
      </div>
      
      <div style={{ padding: '2rem' }}>
        {loading ? (
          <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: '2rem' }}>Yükleniyor...</div></div>
        ) : invoices.length === 0 ? (
          <div className="card">
            <div className="card-body">
              <div className="empty-state">
                <div className="empty-state-icon"><FileText /></div>
                <h3 className="empty-state-title">Henüz fatura yok</h3>
                <p className="empty-state-text">İlk faturanızı oluşturun</p>
                <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                  <Plus size={18} />
                  Fatura Oluştur
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Filtrelenmiş faturalar */}
            {(() => {
              const searchLower = searchTerm.toLowerCase().trim();
              const filteredInvoices = searchTerm 
                ? invoices.filter(inv => 
                    (inv.number || '').toLowerCase().includes(searchLower) ||
                    (inv.client_name || '').toLowerCase().includes(searchLower) ||
                    (inv.vessel_name || '').toLowerCase().includes(searchLower) ||
                    (inv.port || '').toLowerCase().includes(searchLower)
                  )
                : invoices;

              if (searchTerm && filteredInvoices.length === 0) {
                return (
                  <div className="card">
                    <div className="card-body" style={{ textAlign: 'center', padding: '2rem' }}>
                      <Search size={48} style={{ color: '#9ca3af', marginBottom: '1rem' }} />
                      <h3 style={{ color: '#6b7280', marginBottom: '0.5rem' }}>Sonuç bulunamadı</h3>
                      <p style={{ color: '#9ca3af' }}>"{searchTerm}" için eşleşen fatura yok</p>
                      <button 
                        onClick={() => setSearchTerm('')}
                        style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                      >
                        Aramayı Temizle
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <>
                  {/* Arama sonucu bilgisi */}
                  {searchTerm && (
                    <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: '#eff6ff', borderRadius: '8px', color: '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>🔍 "{searchTerm}" için <strong>{filteredInvoices.length}</strong> fatura bulundu</span>
                      <button onClick={() => setSearchTerm('')} style={{ background: 'none', border: 'none', color: '#1e40af', cursor: 'pointer', textDecoration: 'underline' }}>Temizle</button>
                    </div>
                  )}

                  {/* Özet Kartları */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{filteredInvoices.filter(i => i.status === 'pending' && !isOverdue(i)).length}</div>
                      <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Bekleyen</div>
                    </div>
                    <div style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{filteredInvoices.filter(i => i.status === 'partial').length}</div>
                      <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Kısmi Ödeme</div>
                    </div>
                    <div style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{filteredInvoices.filter(i => isOverdue(i)).length}</div>
                      <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Gecikmiş</div>
                    </div>
                    <div style={{ background: 'linear-gradient(135deg, #10b981, #059669)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{filteredInvoices.filter(i => i.status === 'paid').length}</div>
                      <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Ödendi</div>
                    </div>
                  </div>

                  {/* Duruma Göre Gruplandırılmış Tablolar */}
                  {renderInvoiceTable(filteredInvoices.filter(i => isOverdue(i)), '🚨 Gecikmiş Faturalar', '#ef4444')}
                  {renderInvoiceTable(filteredInvoices.filter(i => i.status === 'pending' && !isOverdue(i)), '⏳ Bekleyen Faturalar', '#f59e0b')}
                  {renderInvoiceTable(filteredInvoices.filter(i => i.status === 'partial'), '💳 Kısmi Ödemeli Faturalar', '#8b5cf6')}
                  {renderInvoiceTable(filteredInvoices.filter(i => i.status === 'paid'), '✅ Ödenen Faturalar', '#10b981')}
                </>
              );
            })()}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h2 className="modal-title" data-testid="modal-title">Fatura Oluştur</h2>
              <button className="modal-close" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label className="form-label">Fatura Oluşturma Yöntemi</label>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem 1rem', background: creationType === 'proforma' ? '#e0f2fe' : '#f8fafc', borderRadius: '8px', border: creationType === 'proforma' ? '2px solid #0284c7' : '1px solid #e2e8f0' }}>
                      <input
                        type="radio"
                        value="proforma"
                        checked={creationType === 'proforma'}
                        onChange={(e) => { setCreationType(e.target.value); setSelectedProformaIds([]); }}
                        data-testid="type-proforma"
                      />
                      Tek Proforma
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem 1rem', background: creationType === 'combine' ? '#fef3c7' : '#f8fafc', borderRadius: '8px', border: creationType === 'combine' ? '2px solid #f59e0b' : '1px solid #e2e8f0' }}>
                      <input
                        type="radio"
                        value="combine"
                        checked={creationType === 'combine'}
                        onChange={(e) => { setCreationType(e.target.value); setSelectedProformaIds([]); }}
                        data-testid="type-combine"
                      />
                      Proformaları Birleştir
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem 1rem', background: creationType === 'standalone' ? '#f0fdf4' : '#f8fafc', borderRadius: '8px', border: creationType === 'standalone' ? '2px solid #22c55e' : '1px solid #e2e8f0' }}>
                      <input
                        type="radio"
                        value="standalone"
                        checked={creationType === 'standalone'}
                        onChange={(e) => { setCreationType(e.target.value); setSelectedProformaIds([]); }}
                        data-testid="type-standalone"
                      />
                      Bağımsız Fatura
                    </label>
                  </div>
                </div>

                {creationType === 'proforma' ? (
                  <div>
                    <div className="form-group">
                      <label className="form-label">Onaylı Proforma Seçin *</label>
                      <select
                        className="form-select"
                        value={formData.proforma_id}
                        onChange={(e) => setFormData({ ...formData, proforma_id: e.target.value })}
                        required
                        data-testid="proforma-select"
                      >
                        <option value="">Proforma Seçin</option>
                        {proformas.map(proforma => (
                          <option key={proforma.id} value={proforma.id}>
                            {proforma.number} - {proforma.client_name} ({proforma.currency} {(proforma.total || 0).toFixed(2)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Ödeme Vadesi (Gün)</label>
                        <input
                          type="number"
                          className="form-input"
                          value={formData.due_days}
                          onChange={(e) => setFormData({ ...formData, due_days: parseInt(e.target.value) || 5 })}
                          min="1"
                          data-testid="due-days-input"
                        />
                      </div>
                    </div>
                  </div>
                ) : creationType === 'combine' ? (
                  <div>
                    <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                      <strong>📋 Proformaları Birleştir</strong>
                      <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: '#92400e' }}>
                        Aynı müşteriye ait birden fazla onaylı proformayı tek bir faturada birleştirebilirsiniz.
                      </p>
                    </div>
                    
                    {proformas.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                        Birleştirilecek onaylı proforma bulunmuyor.
                      </div>
                    ) : (
                      <>
                        <div className="form-group">
                          <label className="form-label">Birleştirilecek Proformaları Seçin (En az 2)</label>
                          <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                            {proformas.map(proforma => (
                              <label 
                                key={proforma.id} 
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '0.75rem', 
                                  padding: '0.75rem 1rem',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  background: selectedProformaIds.includes(proforma.id) ? '#fef3c7' : 'transparent'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedProformaIds.includes(proforma.id)}
                                  onChange={() => toggleProformaSelection(proforma.id)}
                                  style={{ width: '18px', height: '18px' }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 600 }}>{proforma.number}</div>
                                  <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                    {proforma.client_name} • {proforma.vessel_name || '-'}
                                  </div>
                                </div>
                                <div style={{ fontWeight: 600, color: '#059669' }}>
                                  {proforma.currency} {(proforma.total || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                        
                        {selectedProformaIds.length > 0 && (
                          <div style={{ marginTop: '1rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                              <span>Seçili Proforma Sayısı:</span>
                              <strong>{selectedProformaIds.length}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                              <span>Müşteri:</span>
                              <strong style={{ color: getSelectedProformasClient() === 'mixed' ? '#dc2626' : '#059669' }}>
                                {getSelectedProformasClient() === 'mixed' ? '⚠️ Farklı müşteriler!' : getSelectedProformasClient()}
                              </strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem' }}>
                              <span>Toplam Tutar:</span>
                              <strong style={{ color: '#059669' }}>
                                {proformas.find(p => selectedProformaIds.includes(p.id))?.currency || 'USD'} {getSelectedProformasTotal().toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                              </strong>
                            </div>
                          </div>
                        )}
                        
                        {getSelectedProformasClient() === 'mixed' && (
                          <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fca5a5', color: '#dc2626', fontSize: '0.875rem' }}>
                            ⚠️ Seçili proformalar farklı müşterilere ait. Birleştirmek için tüm proformalar aynı müşteriye ait olmalıdır.
                          </div>
                        )}
                      </>
                    )}
                    
                    <div className="form-row" style={{ marginTop: '1rem' }}>
                      <div className="form-group">
                        <label className="form-label">Ödeme Vadesi (Gün)</label>
                        <input
                          type="number"
                          className="form-input"
                          value={formData.due_days}
                          onChange={(e) => setFormData({ ...formData, due_days: parseInt(e.target.value) || 5 })}
                          min="1"
                          data-testid="combine-due-days-input"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Client *</label>
                        <select
                          className="form-select"
                          value={formData.client_id}
                          onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                          required
                          data-testid="client-select"
                        >
                          <option value="">Müşteri Seçin</option>
                          {clients.map(client => (
                            <option key={client.id} value={client.id}>{client.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Gemi Adı</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.vessel_name}
                          onChange={(e) => setFormData({ ...formData, vessel_name: e.target.value })}
                          data-testid="vessel-input"
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Liman</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.port}
                          onChange={(e) => setFormData({ ...formData, port: e.target.value })}
                          data-testid="port-input"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Vergi Oranı (%)</label>
                        <input
                          type="number"
                          className="form-input"
                          value={formData.tax_rate}
                          onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                          step="0.01"
                          min="0"
                          data-testid="tax-rate-input"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Para Birimi</label>
                        <select
                          className="form-select"
                          value={formData.currency}
                          onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                          data-testid="currency-select"
                        >
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                          <option value="TRY">TRY</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Payment Terms (Days)</label>
                        <input
                          type="number"
                          className="form-input"
                          value={formData.due_days}
                          onChange={(e) => setFormData({ ...formData, due_days: parseInt(e.target.value) || 5 })}
                          min="1"
                          data-testid="due-days-input"
                        />
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <label className="form-label" style={{ marginBottom: 0 }}>Hizmet Kalemleri *</label>
                        <button type="button" className="btn btn-sm btn-secondary" onClick={addLine} data-testid="add-line-btn">
                          <Plus size={16} /> Satır Ekle
                        </button>
                      </div>
                      
                      {formData.lines.map((line, index) => (
                        <div key={index} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Açıklama"
                            value={line.description}
                            onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                            required
                            data-testid={`line-desc-${index}`}
                          />
                          <input
                            type="number"
                            className="form-input"
                            placeholder="Miktar"
                            value={line.quantity}
                            onChange={(e) => handleLineChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                            step="0.01"
                            min="0"
                            required
                            data-testid={`line-qty-${index}`}
                          />
                          <input
                            type="number"
                            className="form-input"
                            placeholder="Birim Fiyat"
                            value={line.unit_price}
                            onChange={(e) => handleLineChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            step="0.001"
                            min="0"
                            required
                            data-testid={`line-price-${index}`}
                          />
                          <input
                            type="number"
                            className="form-input"
                            placeholder="Toplam"
                            value={(line.total || 0).toFixed(2)}
                            readOnly
                            style={{ background: '#f1f5f9' }}
                            data-testid={`line-total-${index}`}
                          />
                          {formData.lines.length > 1 && (
                            <button 
                              type="button" 
                              className="action-btn" 
                              onClick={() => removeLine(index)}
                              data-testid={`remove-line-${index}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span>Ara Toplam:</span>
                        <strong data-testid="subtotal">{formData.currency} {(subtotal || 0).toFixed(2)}</strong>
                      </div>
                      {(totalVat || 0) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: '#059669' }}>
                          <span>Toplam KDV:</span>
                          <strong data-testid="tax-amount">{formData.currency} {(totalVat || 0).toFixed(2)}</strong>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.25rem', fontWeight: 700 }}>
                        <span>Genel Toplam:</span>
                        <span className="text-primary" data-testid="total">{formData.currency} {(total || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="form-group">
                  <label className="form-label">Notlar</label>
                  <textarea
                    className="form-input"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                    data-testid="notes-input"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" data-testid="save-invoice-btn">
                  Fatura Oluştur
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingInvoice && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h2 className="modal-title">Fatura Düzenle: {editingInvoice.number}</h2>
              <button className="modal-close" onClick={() => { setShowEditModal(false); setEditingInvoice(null); }}>×</button>
            </div>
            <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                <div style={{ background: '#f0f9ff', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Müşteri:</span>
                      <strong style={{ marginLeft: '0.5rem' }}>{editingInvoice.client_name}</strong>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Fatura No:</span>
                      <strong style={{ marginLeft: '0.5rem' }}>{editingInvoice.number}</strong>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div className="form-group">
                    <label className="form-label">Gemi Adı</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.vessel_name}
                      onChange={(e) => setEditFormData({ ...editFormData, vessel_name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Liman</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.port}
                      onChange={(e) => setEditFormData({ ...editFormData, port: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Vade (Gün)</label>
                    <input
                      type="number"
                      className="form-input"
                      value={editFormData.due_days}
                      onChange={(e) => setEditFormData({ ...editFormData, due_days: parseInt(e.target.value) || 5 })}
                      min="1"
                    />
                  </div>
                </div>

                <h4 style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  Hizmet Kalemleri
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addEditLine}>
                    <Plus size={16} /> Satır Ekle
                  </button>
                </h4>
                
                <div className="table-container" style={{ marginBottom: '1.5rem' }}>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '35%' }}>Açıklama</th>
                        <th>Miktar</th>
                        <th>Birim</th>
                        <th>Birim Fiyat</th>
                        <th>KDV</th>
                        <th>KDV %</th>
                        <th>Toplam</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {editFormData.lines.map((line, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              className="form-input"
                              value={line.description}
                              onChange={(e) => handleEditLineChange(index, 'description', e.target.value)}
                              required
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-input"
                              value={line.quantity}
                              onChange={(e) => handleEditLineChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="1"
                              style={{ width: '70px' }}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-input"
                              value={line.unit}
                              onChange={(e) => handleEditLineChange(index, 'unit', e.target.value)}
                              style={{ width: '70px' }}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-input"
                              value={line.unit_price}
                              onChange={(e) => handleEditLineChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.001"
                              style={{ width: '100px' }}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={line.has_vat}
                              onChange={(e) => handleEditLineChange(index, 'has_vat', e.target.checked)}
                            />
                          </td>
                          <td>
                            {line.has_vat && (
                              <input
                                type="number"
                                className="form-input"
                                value={line.vat_rate}
                                onChange={(e) => handleEditLineChange(index, 'vat_rate', parseFloat(e.target.value) || 0)}
                                min="0"
                                max="100"
                                style={{ width: '60px' }}
                              />
                            )}
                          </td>
                          <td>
                            <strong>{editFormData.currency} {(line.total || 0).toFixed(2)}</strong>
                          </td>
                          <td>
                            {editFormData.lines.length > 1 && (
                              <button
                                type="button"
                                className="action-btn action-btn-danger"
                                onClick={() => removeEditLine(index)}
                                style={{ padding: '0.25rem' }}
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span>Ara Toplam:</span>
                    <strong>{editFormData.currency} {(editSubtotal || 0).toFixed(2)}</strong>
                  </div>
                  {editTotalVat > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: '#059669' }}>
                      <span>Toplam KDV:</span>
                      <strong>{editFormData.currency} {(editTotalVat || 0).toFixed(2)}</strong>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.25rem', fontWeight: 700 }}>
                    <span>Genel Toplam:</span>
                    <span className="text-primary">{editFormData.currency} {(editTotal || 0).toFixed(2)}</span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Notlar</label>
                  <textarea
                    className="form-input"
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                    rows="3"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowEditModal(false); setEditingInvoice(null); }}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && selectedInvoice && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2 className="modal-title" data-testid="detail-title">Fatura {selectedInvoice.number}</h2>
              <button className="modal-close" onClick={() => setShowDetail(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Müşteri</div>
                  <div style={{ fontWeight: 600 }}>{selectedInvoice.client_name}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Durum</div>
                  <span className={`badge ${getStatusBadgeClass(isOverdue(selectedInvoice) ? 'Gecikmiş' : selectedInvoice.status)}`}>
                    {isOverdue(selectedInvoice) ? 'Gecikmiş' : selectedInvoice.status}
                  </span>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Gemi</div>
                  <div>{selectedInvoice.vessel_name || '-'}</div>
                  {(() => {
                    const v = vessels.find(v => v.name === selectedInvoice.vessel_name);
                    if (v) {
                      return (
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                          IMO: {v.imo || '-'} | GRT: {v.gross_tonnage || '-'} | NRT: {v.net_tonnage || '-'}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Liman</div>
                  <div>{selectedInvoice.port || '-'}</div>
                </div>
                {/* Geliş/Gidiş Tarihleri */}
                {(() => {
                  const relatedProforma = proformas.find(p => p.id === selectedInvoice.proforma_id);
                  const pc = relatedProforma ? portCalls.find(pc => pc.id === relatedProforma.port_call_id) : null;
                  if (pc) {
                    return (
                      <>
                        <div>
                          <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Geliş (ETA)</div>
                          <div>{pc.eta ? new Date(pc.eta).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Gidiş (ETD)</div>
                          <div>{pc.etd ? new Date(pc.etd).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                        </div>
                      </>
                    );
                  }
                  return null;
                })()}
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Issue Date</div>
                  <div>{new Date(selectedInvoice.issue_date).toLocaleDateString()}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Vade Tarihi</div>
                  <div className={isOverdue(selectedInvoice) ? 'text-danger' : ''}>
                    {new Date(selectedInvoice.due_date).toLocaleDateString()}
                    {isOverdue(selectedInvoice) && ' (Overdue)'}
                  </div>
                </div>
              </div>
              
              <h4 style={{ marginBottom: '1rem' }}>Hizmet Kalemleri</h4>
              <table style={{ marginBottom: '1.5rem' }}>
                <thead>
                  <tr>
                    <th>Açıklama</th>
                    <th>Miktar</th>
                    <th>Birim Fiyat</th>
                    <th>KDV</th>
                    <th>Toplam</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoice.lines.map((line, index) => (
                    <tr key={index}>
                      <td>{line.description}</td>
                      <td>{line.quantity} {line.unit || ''}</td>
                      <td>{selectedInvoice.currency} {(line.unit_price || 0).toFixed(2)}</td>
                      <td>{line.has_vat ? `%${line.vat_rate || 0}` : '-'}</td>
                      <td>{selectedInvoice.currency} {(line.total || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Ara Toplam:</span>
                  <strong>{selectedInvoice.currency} {(selectedInvoice.subtotal || 0).toFixed(2)}</strong>
                </div>
                {(selectedInvoice.total_vat || selectedInvoice.tax_amount || 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: '#059669' }}>
                    <span>Toplam KDV:</span>
                    <strong>{selectedInvoice.currency} {(selectedInvoice.total_vat || selectedInvoice.tax_amount || 0).toFixed(2)}</strong>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.25rem', fontWeight: 700, marginBottom: '1rem' }}>
                  <span>Genel Toplam:</span>
                  <span className="text-primary">{selectedInvoice.currency} {(selectedInvoice.total || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', paddingTop: '1rem', borderTop: '2px solid var(--border)' }}>
                  <span>Ödenen:</span>
                  <strong className="text-success">{selectedInvoice.currency} {(selectedInvoice.paid_amount || 0).toFixed(2)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.25rem', fontWeight: 700 }}>
                  <span>Kalan Bakiye:</span>
                  <span className="text-danger">{selectedInvoice.currency} {(selectedInvoice.balance || 0).toFixed(2)}</span>
                </div>
              </div>
              
              {selectedInvoice.notes && (
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Notlar</div>
                  <div>{selectedInvoice.notes}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;